// R503 (d): the owner's Settings screen reads GET /admin/settings and writes one key at a time
// with PATCH /admin/settings/:key { value } (the contract is R503 (a), Section 0). Both go
// through secureFetch directly, never the offline queue: a setting is a deliberate owner write
// that must say saved or failed where it was made, so the screen disables Save offline instead.
// The registry on the server is the source of every bound; this file carries none.
import { secureFetch } from './auth'
import { failureMessage } from './orders-api'

export type SettingType = 'int' | 'quiet_hours' | 'screens' | 'writeoff_reasons'

export interface IntBounds {
  min: number
  max: number
}

export interface QuietHours {
  start: string
  end: string
}

export interface QuietHoursBounds {
  nullable: boolean
  format: string
  timeZone: string
  keys: string[]
}

export interface ScreenSetting {
  on: boolean
  reasons: string[]
}

export type ScreensValue = Record<string, ScreenSetting>

export interface ScreensBounds {
  screens: string[]
  reasons: string[]
  keys: string[]
}

export interface WriteoffReason {
  code: string
  label: string
  noteRequired: boolean
}

export interface WriteoffBounds {
  minItems: number
  maxItems: number
  keys: string[]
  codePattern: string
  labelMaxLength: number
}

export interface SettingItem {
  key: string
  type: SettingType | string
  value: unknown
  default: unknown
  bounds: Record<string, unknown>
  source: 'row' | 'default'
  updatedAt: string | null
}

export interface SettingsResponse {
  entityId: string
  settings: SettingItem[]
}

export class SettingsApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

// The nav entry and the screen: owner, and architect by rank (the PATCH's own roles). Roles are
// named one by one, never "not hop", so a role added later is not let in by default.
export function canSeeSettings(roles: string[]): boolean {
  return roles.includes('owner') || roles.includes('architect')
}

export async function fetchSettings(): Promise<SettingsResponse> {
  const res = await secureFetch('/api/admin/settings')
  if (!res.ok) throw new SettingsApiError(await failureMessage(res), res.status)
  return res.json() as Promise<SettingsResponse>
}

export async function patchSetting(key: string, value: unknown): Promise<SettingItem> {
  const res = await secureFetch(`/api/admin/settings/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  })
  if (!res.ok) throw new SettingsApiError(await failureMessage(res), res.status)
  return res.json() as Promise<SettingItem>
}
